import * as React from "react"
import {
  Card, CardHeader, CardContent, Typography, Grid, Table, TableRow, TableCell, TableBody,
  Chip, Stack, Button, Box, Divider, IconButton, Tooltip, Paper, LinearProgress, Alert, TextField,
  TablePagination,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  CircularProgress,
} from "@mui/material"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import VisibilityIcon from "@mui/icons-material/Visibility"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import { api } from "../../function/axios"
import { Add as AddIcon } from '@mui/icons-material'

type KBRow = {
  id: string
  name: string
  created_at: number
  status?: string
}

export default function IrbiKnowledgeBasePanel() {
  const [items, setItems] = React.useState<KBRow[]>([])
  const [loading, setLoading] = React.useState<boolean>(true)
  const [uploading, setUploading] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string | null>(null)
  const [info, setInfo] = React.useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [deleteTarget, setDeleteTarget] = React.useState<KBRow | null>(null)

  const [page, setPage] = React.useState(0)

  const [addOpen, setAddOpen] = React.useState(false)
  const [addTitle, setAddTitle] = React.useState<string>('')
  const [addText, setAddText] = React.useState<string>('')
  const [addSubmitting, setAddSubmitting] = React.useState(false)
  const [addError, setAddError] = React.useState<string | null>(null)

  const rowsPerPage = 5

  const pagedItems = React.useMemo(
    () => items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [items, page],
  )

  React.useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(items.length / rowsPerPage) - 1)
    if (page > maxPage) setPage(maxPage)
  }, [items, page])

  const refetch = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api({
        method: 'GET',
        url: '/ai-assistant/irbi_kb',
      })
      if (!res.code) throw new Error(`GET KnowledgeBase → ${res.code}`)
      const data: Array<{
        id: string
        name: string
        created_at: number
      }> = await res?.data

      const mapped: KBRow[] = data.map(d => ({
        id: d.id,
        name: d.name,
        created_at: d.created_at ?? 0,
      }))
      setItems(mapped)
    } catch (e: any) {
      setError(e.message ?? String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { void refetch() }, [refetch])

  const handleAddKnowledgeBase = () => {
    setAddError(null)
    setAddOpen(true)
  }

  const handleAddCancel = () => {
    setAddOpen(false)
    setAddTitle('')
    setAddText('')
    setAddError(null)
  }

  const handleAddSubmit = async () => {
    if (!addTitle.trim() || !addText.trim()) {
      setAddError('Please enter title and content.')
      return
    }

    try {
      setAddSubmitting(true)
      setAddError(null)

      const payload = {
        title: addTitle.trim(),
        text: addText.trim(),
      }

      const res = await api({
        method: "POST",
        url: "/ai-assistant/irbi_kb",
        headers: { "Content-Type": "application/json" },
        body: payload,
      })

      if (!res.code) throw new Error('Failed to add item')
      await refetch()
      handleAddCancel()
    } catch (e: any) {
      setAddError(e?.message || 'Add failed')
    } finally {
      setAddSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      setError(null)
      setItems(prev => prev.filter(i => i.id !== deleteTarget.id))
      await api({
        method: "DELETE",
        url: `/ai-assistant/irbi_kb/${deleteTarget.id}`,
      })
      setInfo(`Removed: ${deleteTarget.name}`)
      await refetch()
      setConfirmOpen(false)
      setDeleteTarget(null)
    } catch (err: any) {
      setError(err.message ?? String(err))
      await refetch()
    } finally {
      setConfirmOpen(false)
      setDeleteTarget(null)
    }
  }

  const formatUnixUtc = (ts: number | string) => {
    const n = Number(ts)
    const d = new Date(n < 1e12 ? n * 1000 : n)
    const pad = (x: number) => x.toString().padStart(2, '0')
    return `${pad(d.getUTCDate())}.${pad(d.getUTCMonth() + 1)}.${d.getUTCFullYear()} ` +
      `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`
  }

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 0 }}>
      <CardHeader
        title={<Typography variant="subtitle1" fontWeight={600}>Knowledge Base</Typography>}
        subheader={<Typography variant="caption" color="text.secondary">Upload files and manage indexed items</Typography>}
        sx={{ pb: 0.5 }}
        action={
          <Button
            variant="contained"
            color="primary"
            style={{
              width: 40,
              height: 40,
              minWidth: 0,
              borderRadius: '50%',
              padding: 0,
              alignItems: 'left',
            }}
            onClick={handleAddKnowledgeBase}
          >
            <AddIcon style={{
              width: 20,
              height: 20,
            }} />
          </Button>
        }
      />
      {(loading || uploading) && <LinearProgress />}
      <Divider />

      <CardContent>
        <Stack spacing={2} sx={{ mb: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {info && <Alert severity="success">{info}</Alert>}
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 2 }}>

              <Stack direction="row" spacing={2}>
                <Typography variant="caption" sx={{ width: "25%", textAlign: "center" }}>
                  Name
                </Typography>
                <Typography variant="caption" sx={{ width: "25%", textAlign: "center" }}>
                  Id
                </Typography>
                <Typography variant="caption" sx={{ width: "25%", textAlign: "center" }}>
                  Created At
                </Typography>
                <Typography variant="caption" sx={{ width: "25%", textAlign: "center" }}>
                  Actions
                </Typography>
              </Stack>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ "& td, & th": { border: 0 } }}>
                  <TableBody>
                    {pagedItems.map((row) => (
                      <TableRow key={row.id} sx={{ "&:not(:last-of-type) td": { borderBottom: theme => `1px solid ${theme.palette.divider}` } }}>
                        <TableCell align="center" sx={{ width: '25%' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {row.name.length > 15 ? (
                              <Tooltip title={row.name} arrow>
                                <Chip size="small" label={`${row.name.slice(0, 15)}…`} color="primary" />
                              </Tooltip>
                            ) : (
                              <Chip size="small" label={row.name} color="primary" />
                            )}
                          </Box>
                        </TableCell>

                        <TableCell align="center" sx={{ width: '25%' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {row.id.length > 15 ? (
                              <Tooltip title={row.id} arrow>
                                <Chip
                                  size="small"
                                  label={`${row.id.slice(0, 15)}…`}
                                  color="primary"
                                />
                              </Tooltip>
                            ) : (
                              <Chip size="small" label={row.id} color="primary" />
                            )}
                          </Box>
                        </TableCell>

                        <TableCell sx={{ width: "25%", textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">{formatUnixUtc(row.created_at)}</Typography>
                        </TableCell>
                        <TableCell sx={{ width: "25%" }}>
                          <Stack direction="row" spacing={0} justifyContent="center">
                            <Tooltip title="Preview">
                              <span>
                                <IconButton size="small" disabled >
                                  <VisibilityIcon fontSize="small" color="primary" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Remove">
                              <span>
                                <IconButton size="small"
                                  disabled={loading || uploading}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setDeleteTarget(row)
                                    setConfirmOpen(true)
                                  }}
                                >
                                  <DeleteOutlineIcon fontSize="small" color="error" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={items.length}
                page={page}
                onPageChange={(_e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[]}
                labelRowsPerPage="Rows per page"
                sx={{
                  borderTop: theme => `1px solid ${theme.palette.divider}`,
                  px: 2,
                }}
              />

            </Paper>
          </Grid>

        </Grid>
      </CardContent>
      <Dialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false)
          setDeleteTarget(null)
        }}
      >
        <DialogTitle>Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete Knowledge Base item (<b>{deleteTarget?.name}</b>)?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmOpen(false)
              setDeleteTarget(null)
            }}
          >
            No
          </Button>
          <Button color="error" onClick={handleDelete}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addOpen}
        onClose={addSubmitting ? undefined : handleAddCancel}
        fullWidth
        maxWidth="sm"
        keepMounted
        sx={{
          '& .MuiDialog-paper': {
            width: '50vw',
            maxWidth: '75vw',
            height: '25vw',
          },
        }}
      >
        <DialogTitle>Add to Knowledge Base</DialogTitle>

        <DialogContent dividers>
          {addError && <Alert severity="error" sx={{ mb: 2 }}>{addError}</Alert>}
          <TextField
            autoFocus
            fullWidth
            placeholder="Enter a title…"
            value={addTitle}
            onChange={(e) => setAddTitle(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            disabled={addSubmitting}
            sx={{ mb: 2 }}
          />
          <TextField
            multiline
            placeholder="Add to Knowledge Base..."
            value={addText}
            onChange={(e) => setAddText(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            disabled={addSubmitting === true ? true : false}
            inputProps={{ spellCheck: false }}
            minRows={10}
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                height: "100%",
                alignItems: "flex-start",
              },
            }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleAddCancel} disabled={addSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddSubmit}
            disabled={addSubmitting || !addText.trim()}
            startIcon={addSubmitting ? <CircularProgress size={16} /> : null}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

    </Card>
  )
}
